import { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Card,
  CardContent,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Alert
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import ScrollToTop from "../nav/ScrollToTop";
import InputAdornment from '@mui/material/InputAdornment';

import SfsHelp from "../sfs/SfsHelp";
import SelectPaymentCard from "./form/SelectPaymentCard";
import { useArrangement } from "../../context/ArrangementProvider";
import { usePayment } from "../../context/PaymentProvider";


function PaymentQuestions({
  balance,
  handleNext,
  paymentAmount,
  updatePaymentAmount,
  setHideRightColumn,
  showSfsHelp,
  setShowSfsHelp,
  selectedCard,
  setSelectedCard,
  saveNewCard,
  setSaveNewCard
}) {
  const [showAmountInput, setShowAmountInput] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [rawAmountInput, setRawAmountInput] = useState("");

  const { hasArrangement, lastSavedPlan } = useArrangement();

  const instalmentAmount = lastSavedPlan?.instalmentAmount || 0;

  const {
    paymentAmountOption,
    setPaymentAmountOption,
    priorityBillsOptions,
    priorityBillsAnswer,
    setPriorityBillsAnswer,
    sourceOfFundsOptions,
    sourceOfFundsAnswer,
    setSourceOfFundsAnswer,
    otherCreditorsOptions,
    otherCreditorsAnswer,
    setOtherCreditorsAnswer,
    furtherBorrowing,
    noCreditorNoPlan,
  } = usePayment();

  const navigate = useNavigate();

  function handleSfsHelp() {
    if (
      priorityBillsAnswer === noCreditorNoPlan ||
      sourceOfFundsAnswer === furtherBorrowing ||
      otherCreditorsAnswer === noCreditorNoPlan) {
      setShowSfsHelp(priorityBillsAnswer === noCreditorNoPlan || otherCreditorsAnswer === noCreditorNoPlan)    
      setHideRightColumn(true);
    } else {
      setHideRightColumn(false);
      handleNext();
    }
  }

  const changePaymentOption = (event) => {
    const option = event.target.value;
    if (option === "Other Amount") {
      setShowAmountInput(true);
      setPaymentAmountOption(option);
      updatePaymentAmount(0);
    } else {
      setShowAmountInput(false);
      setPaymentAmountOption(option);
      updatePaymentAmount(instalmentAmount);
    }
  }

  const handleAmountChange = (e) => {
    const val = e.target.value;

    // Allow: digits, 1 decimal point, up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setRawAmountInput(val);

      const parsed = parseFloat(val);
      if (!isNaN(parsed)) {
        if (parsed > balance) {
          setShowSnackbar(true);
        } else {
          setShowSnackbar(false);
          updatePaymentAmount(parsed);
        }
      } else {
        updatePaymentAmount(0);
        setShowSnackbar(false);
      }
    }
  };

  const handleBlur = () => {
    const parsed = parseFloat(rawAmountInput);
    if (!isNaN(parsed)) {
      const fixed = parsed.toFixed(2);
      setRawAmountInput(fixed);
      updatePaymentAmount(parseFloat(fixed));
    } else {
      setRawAmountInput("");
      updatePaymentAmount(0);
    }
  };

  useEffect(() => {
    setRawAmountInput(paymentAmount > 0 ? String(paymentAmount) : "");
  }, [paymentAmount]);

  const isContinueButtonDisabled = () => {
    return (
      paymentAmount <= 0 ||
      !priorityBillsAnswer ||
      !sourceOfFundsAnswer ||
      !otherCreditorsAnswer ||
      (showSfsHelp && !checkboxChecked) ||
      showSnackbar
    );
  };

  return (
    showSfsHelp ?    
      <>
        <ScrollToTop />
        <SfsHelp />
        <Card sx={{ display: "flex", alignItems: "flex-start" }}>
          <CardContent sx={{
            display: 'flex', padding: 1, "&:last-child": {
              paddingBottom: 1
            }
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkboxChecked}
                  onChange={(event) => setCheckboxChecked(event.target.checked)}
                />
              }
              label={
                <Typography
                  component="span"
                  sx={{
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    whiteSpace: 'normal',
                    lineHeight: 1.5,
                    display: 'inline',
                  }}
                >
                  I confirm that I have considered my need for additional support and I am happy to make a payment towards my account.
                </Typography>
              }
              sx={{
                alignItems: 'center',
                textAlign: 'left',
                margin: 0,
              }}
            />

          </CardContent>

        </Card>
        <Box sx={{ pt: 4, textAlign: { xs: "center", md: "left" } }}>
          <Button
            onClick={() => navigate("/")}
            variant='outlined'
            sx={{
              textTransform: "none",
              borderRadius: 0,
              marginRight: "15px",
              width: { xs: "5em", lg: "10em" },
              backgroundColor: "transparent",
              color: "secondary.main",
              borderColor: "secondary.main",
              "&:hover": {
                backgroundColor: "#fff",
                color: "secondary.main",
              },
            }}
          >
            Back
          </Button>
          <Button
            id='continue-btn'
            disabled={isContinueButtonDisabled()}
            onClick={handleNext}
            variant='contained'
            sx={{
              textTransform: "none",
              borderRadius: 0,
              width: { xs: "5em", lg: "10em" },
              backgroundColor: "primary.dark",
              color: "primary.contrastText",
              "&:hover": {
                backgroundColor: "primary.light",
                color: "primary.dark",},
              }}
            >
              Continue
            </Button>
        </Box>
      </>
      :
    <Card
      sx={{
        borderRadius: { xs: 0, md: 0 },
          pb: 3,
          width: "100%",
          minWidth: { xs: "111%", md: "100%" }
      }}
    >
      <Box>
        <CardContent
          sx={{
            textAlign: "left",
            borderRadius: "0px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Form content here */}
          <Stack
            spacing={2}
            direction={{
              xs: "column",
              md: "row",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography
                component='label'
                htmlFor='balance'
                fontSize={14}
                paddingY={0.5}
              >
                Balance
              </Typography>
              <Box
                id='balance'
                sx={{
                  backgroundColor: "#E2E2E2",
                  borderRadius: 0,
                  border: "1px solid #BBBBBB",
                  p: 1,
                  fontWeight: "bold",
                  fontSize: 16,
                }}
              >{`£${balance.toFixed(2)}`}</Box>
            </Box>

            <Box sx={{ width: "100%" }}>
              <Box paddingBottom={1}>
                <Typography
                  component='label'
                  htmlFor='select-amount'
                  fontSize={14}
                  paddingY={0.5}
                >
                  Select payment amount
                </Typography>
                <Select
                  id='amount-select'
                  fullWidth
                  value={paymentAmountOption}
                  displayEmpty
                  sx={{ borderRadius: "0px" }}
                  onChange={changePaymentOption}
                  size='small'
                  renderValue={(selected) => {
                    if (selected === "") {
                      return (
                        <Typography fontWeight='bold'>
                          Choose an option
                        </Typography>
                      );
                    } else {
                      return selected;
                    }
                  }}
                >
                  {hasArrangement && (
                    <MenuItem
                      id='agreed-repayment'
                      value={`Agreed repayment`}
                    >{`Agreed repayment - £${instalmentAmount.toFixed(
                      2
                    )}`}</MenuItem>
                  )}
                  <MenuItem id='other-amount' value='Other Amount'>
                    Other amount
                  </MenuItem>
                </Select>
              </Box>
              {/* End of select payment amount */}

              
              {(showAmountInput || !hasArrangement) && (
                  <Box>
                  <Typography
                    component='label'
                    htmlFor='amount'
                    fontSize={14}
                    paddingY={0.5}
                  >
                    Amount
                  </Typography>

                    <TextField
                      id="amount"
                      type="text"
                      fullWidth
                      size="small"
                      InputProps={{
                        sx: { borderRadius: 0 },
                        startAdornment: <InputAdornment position="start">£</InputAdornment>,
                      }}
                      inputProps={{
                        inputMode: 'decimal',
                        pattern: '[0-9]*[.,]?[0-9]{0,2}'
                      }}
                      value={rawAmountInput}
                      onChange={handleAmountChange}
                      onBlur={handleBlur}
                    />
                    {showSnackbar && (
                      <Box sx={{ mt: 1 }}>
                        <Alert
                          severity="error"
                          variant="filled"
                          onClose={() => {
                            setShowSnackbar(false);
                            setRawAmountInput("");
                            updatePaymentAmount(0);
                          }}
                        >
                          You can't enter a value that exceeds your current balance
                        </Alert>
                      </Box>
                    )}

                  </Box>

              )}
              {/* End of enter payment amount */}
            </Box>
          </Stack>

          <SelectPaymentCard
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
            saveNewCard={saveNewCard}
            setSaveNewCard={setSaveNewCard}
          />

          {/* Priority bills */}
          <Box sx={{ pt: 3, pb: 1, width: "100%" }}>
            <Typography fontSize={16} fontWeight='bold'>
              Are your priority bills up to date?
            </Typography>
            <Typography fontSize={14} paddingY={0.5}>
              e.g. your mortgage, your rent, and/or your energy bills such as
              your Gas, Electricity & Council Tax?
            </Typography>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "49%" } }}>
            <Select
              id='priority-bills-select'
              fullWidth
              displayEmpty
              value={priorityBillsAnswer || ""}
              onChange={(event) => setPriorityBillsAnswer(event.target.value)}
              size='small'
              renderValue={(selected) => {
                if (selected === "") {
                  return (
                    <Typography fontWeight='bold'>Please select</Typography>
                  );
                } else {
                  return selected;
                }
              }}
            >
              {priorityBillsOptions.map((item) => (
                <MenuItem key={item.id} id={item.id} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* End of priority bills */}

          {/* Source of funds */}
          <Box sx={{ pt: 4, pb: 1, width: "100%" }}>
            <Typography fontSize={16} fontWeight='bold'>
              How will you fund this payment?
            </Typography>
            <Typography fontSize={14} paddingY={0.5}>
              Where will the payment come from?
            </Typography>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "49%" } }}>
            <Select
              id='source-of-funds-select'
              fullWidth
              displayEmpty
              value={sourceOfFundsAnswer || ""}
              onChange={(event) => setSourceOfFundsAnswer(event.target.value)}
              size='small'
              renderValue={(selected) => {
                if (selected === "") {
                  return (
                    <Typography fontWeight='bold'>Please select</Typography>
                  );
                } else {
                  return selected;
                }
              }}
            >
              {sourceOfFundsOptions.map((item) => (
                <MenuItem key={item.id} id={item.id} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* End of source of funds */}

          {/* Other creditors */}
          <Box sx={{ pt: 4, pb: 1, width: "100%" }}>
            <Typography fontSize={16} fontWeight='bold'>
              Are you up to date with any other creditors?
            </Typography>
            <Typography fontSize={14} paddingY={0.5}>
              Are there other creditors e.g. Are you repaying another loans at the moment or repaying other forms of lending?
            </Typography>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "49%" } }}>
            <Select
              id='other-creditors-select'
              fullWidth
              displayEmpty
              value={otherCreditorsAnswer || ""}
              onChange={(event) => setOtherCreditorsAnswer(event.target.value)}
              size='small'
              renderValue={(selected) => {
                if (selected === "") {
                  return (
                    <Typography fontWeight='bold'>Please select</Typography>
                  );
                } else {
                  return selected;
                }
              }}
            >
              {otherCreditorsOptions.map((item) => (
                <MenuItem key={item.id} id={item.id} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* End of other creditors */}

          {/* Call To Action */}
          <Box sx={{ pt: 4, textAlign: { xs: "center", md: "left" } }}>
            <Button
              onClick={() => navigate("/")}
              variant='outlined'
              sx={{
                textTransform: "none",
                borderRadius: 0,
                marginRight: "15px",
                width: { xs: "5em", lg: "10em" },
                backgroundColor: "transparent",
                color: "secondary.main",
                borderColor: "secondary.main",
                "&:hover": {
                  backgroundColor: "#fff",
                  color: "secondary.main",
                },
              }}
            >
              Back
            </Button>
            <Button
              id='continue-btn'
              disabled={isContinueButtonDisabled()}
              onClick={handleSfsHelp}
              variant='contained'
              sx={{
                textTransform: "none",
                borderRadius: 0,
                width: { xs: "5em", lg: "10em" },
                backgroundColor: "primary.dark",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.light",
                  color: "primary.dark",
                  }
                }}
            >
              Continue
            </Button>
          </Box>
        </CardContent>
        </Box>
      </Card>
  )}
  
export default PaymentQuestions;
